import { Form, Select, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { PlusButton } from 'src/components/common/PlusButton/PlusButton';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { getApplicationStatuses } from 'src/pages/LoanApplicationsPage/ApplicationStatusInfoMap';
import {
    LoanApplicationFilters,
    LoanApplicationsStore,
} from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import { DateHelper } from 'src/utils/DateHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const LoanApplicationsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <StyledHeaderWrapper>
            <SearchPanel
                inputSearchFormName={formNames.getPath('filterSearch')}
                store={store}
                renderPanel={(props) => (
                    <LoanApplicationsFilterPanel {...props} />
                )}
                inputSearchPlaceholder={t(
                    'Page.LoanApplication.AsideHeader.Search.Placeholder',
                )}
            />
            {UserStore.can.manage.applications && (
                <Tooltip
                    title={t(
                        'Page.LoanApplication.AsideHeader.CreateNewItem.Tooltip',
                    )}
                >
                    <PlusButton
                        onClick={() => {
                            const createLink =
                                EntityLinks.applications.process?.(
                                    createApplicationProcessName,
                                );
                            createLink && Router.navigate(createLink);
                        }}
                        data-testid="header-panel-create-btn"
                    />
                </Tooltip>
            )}
        </StyledHeaderWrapper>
    );
});

const StyledHeaderWrapper = styled.header`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
`;

const LoanApplicationsFilterPanel = observer(
    (props: SearchPanelFilterFormProps) => {
        const statuses = getApplicationStatuses();
        const { t } = useTranslation();

        return (
            <SearchPanelFilterForm
                {...props}
                onFinish={(values: FilterWithDateRange) => {
                    store.pager.reset();
                    store.filterCriteria?.applyFilter(
                        convertFormValuesToFilterCriteria(values),
                    );
                    store.loadList();
                }}
            >
                <StyledSelect
                    label={t('Page.LoanApplication.Filter.Status')}
                    name={formNames.getPath('filterStatuses')}
                >
                    <Select
                        mode="multiple"
                        allowClear
                        data-testid="filterStatuses"
                    >
                        {statuses.map((item) => (
                            <Select.Option
                                value={item.id}
                                key={item.id}
                                data-testid={item.id}
                            >
                                {t(`data:status.application.${item.id}`)}
                            </Select.Option>
                        ))}
                    </Select>
                </StyledSelect>
                <Form.Item
                    label={t('Page.LoanApplication.Filter.IssuedAt')}
                    name={formNames.getPath(dateRangeName)}
                >
                    <DateRangePicker data-testid={dateRangeName} />
                </Form.Item>
            </SearchPanelFilterForm>
        );
    },
);

const store = LoanApplicationsStore;
const formNames = FormHelper.createNameProvider<FilterWithDateRange>();
const dateRangeName = 'filterRequestedAt';
const createApplicationProcessName = 'SendApplicationFromBackofficeProcess';

type FilterWithDateRange = Omit<
    LoanApplicationFilters,
    'filterRequestedAtFrom' | 'filterRequestedAtTo'
> & {
    [dateRangeName]: [moment.Moment, moment.Moment];
};

const convertFormValuesToFilterCriteria = (
    values: FilterWithDateRange,
): LoanApplicationFilters => {
    const { filterRequestedAt, ...otherValues } = values;
    const [filterRequestedAtFrom, filterRequestedAtTo] = filterRequestedAt || [
        undefined,
        undefined,
    ];

    return {
        ...otherValues,
        filterRequestedAtFrom: filterRequestedAtFrom
            ? DateHelper.formatToISO(filterRequestedAtFrom.startOf('day'))
            : undefined,
        filterRequestedAtTo: filterRequestedAtTo
            ? DateHelper.formatToISO(filterRequestedAtTo.endOf('day'))
            : undefined,
    };
};

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: 2.7rem;
        border-radius: 0.5rem;
    }
`;
